<template>
  <div class="results11-wrap">

    <div class="topbar">
      <div class="topbar-left">
        <span class="topbar-emoji">🌍</span>
        <span><strong>SEQ</strong> · Your Class's Food Adventure</span>
      </div>
      <div>CLASS 7B · 09.05.2026</div>
    </div>

    <!-- HERO -->
    <div class="hero">
      <div class="float float-1">🥕</div>
      <div class="float float-2">🌽</div>
      <div class="float float-3">🍎</div>
      <div class="float float-4">🥦</div>

      <span class="hero-tag">⚡ Your Food Adventure ⚡</span>
      <h1 class="hero-title">
        <span class="word-1">What</span> <span class="word-2">your</span> <span class="word-3">lunch</span><br>
        is <em>doing</em> to <em>the world</em>.
      </h1>
      <p class="hero-sub">
        Spoiler: it's wild. Every meal you eat is connected to farmers in 15 countries, hidden water in 7 oceans, and animals you'll probably never meet. Let's go on a journey.
      </p>

      <div class="hero-class-card">
        <div class="hero-class-card-num">22</div>
        <div class="hero-class-card-label">
          <strong>students in 7B</strong>
          took the quiz and made this report
        </div>
      </div>
    </div>

    <!-- 01 BIG NUMBERS -->
    <section class="section bg-sun">
      <div class="section-inner">
        <span class="section-num">📊 The Big Numbers</span>
        <h2 class="section-title">
          Your class eats stuff <em class="pop-plum">from everywhere</em>. Here's the proof.
        </h2>
        <p class="section-lede">
          We added up everything 22 of you said you eat in a week. Then we asked the science: how far did your food travel, how much water did it drink, how many trees did it cost? The answers are bigger than you'd think.
        </p>

        <div class="big-stats-row">
          <div v-for="s in bigStats" :key="s.value" class="big-stat">
            <div class="big-stat-emoji">{{ s.emoji }}</div>
            <div class="big-stat-value" :class="s.color">{{ s.value }}</div>
            <div class="big-stat-label">
              <strong>{{ s.label }}</strong>
              {{ s.body }}
            </div>
          </div>
        </div>

        <div class="poster-number">52%</div>
        <p class="poster-caption">
          of your class's food came from <em class="poster-caption-em">outside Switzerland</em>. More than half!
        </p>
      </div>
    </section>

    <!-- 02 COMIC STRIP -->
    <section class="section bg-paper">
      <div class="section-inner">
        <span class="section-num">🎬 Comic Strip</span>
        <h2 class="section-title">
          The <em class="pop-tomato">secret life</em> of a Swiss apple.
        </h2>
        <p class="section-lede">
          Most of you eat apples. Cool. But how does an apple actually get from a tree to your lunchbox? Here's the 4-panel story.
        </p>

        <div class="comic-strip">
          <div class="comic-row">
            <div v-for="(panel, i) in comicPanels" :key="i" class="comic-panel">
              <div class="comic-panel-num">{{ i + 1 }}</div>
              <div class="comic-panel-emoji">{{ panel.emoji }}</div>
              <div class="comic-panel-text">
                <strong>{{ panel.title }}</strong><br>
                {{ panel.body }}
              </div>
            </div>
          </div>
        </div>

        <div class="divider">⭐ ⭐ ⭐</div>

        <p class="apple-compare">
          <strong>Swiss apples travel about 80 km.</strong><br>
          An apple from Argentina? <strong class="apple-compare-far">12,000 km.</strong><br>
          That's 150 times further. Same apple. Very different story.
        </p>
      </div>
    </section>

    <!-- 03 PRODUCER TRADING CARDS -->
    <section class="section bg-dark">
      <div class="section-inner">
        <span class="section-num">⚡ Meet the People</span>
        <h2 class="section-title">
          <em class="pop-sun">Trading cards</em>: the real humans behind your food.
        </h2>
        <p class="section-lede">
          Your food doesn't come from a supermarket. It comes from people. Here are four of them, working right now, somewhere on Earth, to feed you and millions of other kids.
        </p>

        <div class="cards-grid">
          <div v-for="p in producers" :key="p.name" class="producer-card">
            <div class="producer-card-header" :class="p.headerClass">
              <div class="producer-card-flag">{{ p.flag }}</div>
              <div class="producer-card-rarity">{{ p.rarity }}</div>
            </div>
            <div class="producer-card-body">
              <div class="producer-avatar"><span class="producer-avatar-emoji">{{ p.avatar }}</span></div>
              <div>
                <div class="producer-name">{{ p.name }}</div>
                <div class="producer-role">{{ p.role }}</div>
                <div class="producer-stats">
                  <div v-for="(stat, i) in p.stats" :key="i" class="producer-stat">
                    <div class="producer-stat-num">{{ stat.num }}</div>
                    <div class="producer-stat-label">{{ stat.label }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="producer-card-quote">"{{ p.quote }}"</div>
          </div>
        </div>

        <p class="producer-footer">
          These are real types of people, doing real jobs, right now, while you read this report. Pretty wild, right?
        </p>
      </div>
    </section>

    <!-- 04 LIVE WIDGET -->
    <section class="section bg-sky">
      <div class="section-inner">
        <span class="section-num">⏰ Live Right Now</span>
        <h2 class="section-title">
          As you read this, <em class="pop-sun">somewhere</em> in the world...
        </h2>
        <p class="section-lede">
          The food on your plate exists in two places at once: yours, and where it came from. Here's what's happening right now in three of those places.
        </p>

        <div class="live-widget">
          <div v-for="place in livePlaces" :key="place.key" class="live-place">
            <div class="live-place-flag">{{ place.flag }}</div>
            <div class="live-place-name">{{ place.name }}</div>
            <div class="live-place-time">{{ liveTimes[place.key] || '—:—' }}</div>
            <div class="live-place-weather">{{ place.weather }}</div>
            <div class="live-place-action">{{ place.action }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 05 WORLD MAP -->
    <section class="section bg-leaf">
      <div class="section-inner">
        <span class="section-num">🗺️ World Tour</span>
        <h2 class="section-title">
          Your <em class="pop-sun">food</em> visited more countries than you have.
        </h2>
        <p class="section-lede">
          We mapped where the food in your class's week came from. It's a lot of places. Each tile = one country that helped feed you.
        </p>

        <div class="country-grid">
          <div v-for="c in countries" :key="c.name" class="country-tile">
            <div class="country-distance">{{ c.distance }}</div>
            <div class="country-flag">{{ c.flag }}</div>
            <div class="country-name">{{ c.name }}</div>
            <div class="country-foods">{{ c.foods }}</div>
          </div>
        </div>

        <p class="world-footer">
          That's at least 9 countries. In ONE week. From ONE class.
        </p>
      </div>
    </section>

    <!-- 06 DID YOU KNOW -->
    <section class="section bg-paper">
      <div class="section-inner">
        <span class="section-num">🤯 Mind-Blow Facts</span>
        <h2 class="section-title">
          6 things about food <em class="pop-tomato">most adults</em> don't even know.
        </h2>
        <p class="section-lede">
          You're about to learn stuff your parents probably don't know. Use this power wisely.
        </p>

        <div class="dyk-grid">
          <div v-for="(d, i) in dyks" :key="d.title" class="dyk-card">
            <div class="dyk-badge">Fact #{{ i + 1 }}</div>
            <div class="dyk-emoji">{{ d.emoji }}</div>
            <div class="dyk-title">{{ d.title }}</div>
            <div class="dyk-body" v-html="d.body"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- 07 CLASS POLL -->
    <section class="section bg-pink">
      <div class="section-inner">
        <span class="section-num">📊 Class Poll</span>
        <h2 class="section-title">
          Here's what <em class="pop-plum">your class</em> actually answered.
        </h2>
        <p class="section-lede">
          We don't know who said what. We DO know how many of you said each thing. Some answers were almost unanimous. Others split you straight down the middle.
        </p>

        <div v-for="poll in polls" :key="poll.question" class="poll-card">
          <div class="poll-question">{{ poll.question }}</div>
          <div v-for="(row, i) in poll.rows" :key="i" class="poll-bar-row">
            <div class="poll-bar-label">{{ row.label }}</div>
            <div class="poll-bar-track"><div class="poll-bar-fill" :class="row.cls" :style="{ width: row.width + '%' }"></div></div>
            <div class="poll-bar-count">{{ row.count }}</div>
          </div>
          <p class="poll-note">{{ poll.note }}</p>
        </div>
      </div>
    </section>

    <!-- 08 AI STORY -->
    <section class="section bg-paper">
      <div class="section-inner">
        <span class="section-num">✨ AI Magic</span>
        <h2 class="section-title">
          An <em class="pop-plum">AI</em> wrote your class a story.
        </h2>
        <p class="section-lede">
          We asked an AI to write a short story based on your real class answers. Every class gets a different story.
        </p>

        <div class="ai-story">
          <span class="ai-story-tag">✨ Generated for Class 7B</span>
          <h3 class="ai-story-title">The Day Class 7B's Lunch Trays Connected to 9 Countries</h3>
          <div class="ai-story-body">
            <p>Tuesday lunch. 22 of you sit down with sandwiches, apples, water bottles, maybe a chocolate or two. Looks normal. <em>It's not.</em></p>
            <p>Your sandwich bread? Swiss wheat, milled 40 km away. The cheese inside? Heinz's cows in Emmental. The tomato slice? Probably Spanish (sorry). The chocolate? Cocoa from Ghana. The apple? <em>Plot twist:</em> a Swiss tree, three hours away. And those water bottles? Tap water from Switzerland's alps. Footprint: practically zero.</p>
            <p>Here's the wild part: <em>13 of you have never tried lentils.</em> The same lentils that grow in less water, less land, and basically save the planet. We're not saying you have to fall in love with lentil soup. We're just saying... maybe try one. Once. For science.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 09 BADGES -->
    <section class="section bg-plum">
      <div class="section-inner">
        <span class="section-num">🏆 Achievements</span>
        <h2 class="section-title">
          Badges your class <em class="pop-sun">already unlocked</em>.
        </h2>
        <p class="section-lede">
          Based on your real answers, here are the achievements your class has earned. Plus a few that are still locked, in case you want to go for them.
        </p>

        <div class="badges-grid">
          <div v-for="b in badges" :key="b.name" class="badge" :class="b.locked ? 'locked' : 'unlocked'">
            <div class="badge-emoji">{{ b.emoji }}</div>
            <div class="badge-name">{{ b.name }}</div>
            <div class="badge-desc">{{ b.desc }}</div>
            <div class="badge-status">{{ b.status }}</div>
          </div>
        </div>

        <p class="badges-footer">4 badges down, 4 to go. Your class is doing great.</p>
      </div>
    </section>

    <!-- 10 SPIN WHEEL -->
    <section class="section bg-paper">
      <div class="section-inner">
        <span class="section-num">🎯 Class Challenge</span>
        <h2 class="section-title">
          Spin the <em class="pop-tomato">challenge wheel</em>.
        </h2>
        <p class="section-lede">
          Click the button. The wheel picks a random food challenge for your class to try this week. Easy mode, medium mode, or hard mode.
        </p>

        <div class="wheel-card">
          <div class="wheel-emoji">🎯</div>
          <div class="wheel-title">CLASS CHALLENGE GENERATOR</div>
          <div class="wheel-sub">Click the button. Get a random food mission.</div>
          <button class="wheel-button" @click="spinWheel">🎲 SPIN THE WHEEL</button>
          <div class="wheel-result">
            <span class="wheel-result-emoji">{{ wheelResult.emoji }}</span>
            {{ wheelResult.text }}
          </div>
        </div>
      </div>
    </section>

    <!-- 11 MISSIONS -->
    <section class="section bg-coral">
      <div class="section-inner">
        <span class="section-num">🚀 Choose Your Mission</span>
        <h2 class="section-title">
          Got <em class="pop-sun">superpowers</em>? Use them.
        </h2>
        <p class="section-lede">
          Here are 9 small things you can do that actually change the world. Each one is worth points. Pick the ones that match your style.
        </p>

        <div class="mission-grid">
          <div v-for="m in missions" :key="m.title" class="mission" :class="m.level">
            <div class="mission-emoji">{{ m.emoji }}</div>
            <div class="mission-title">{{ m.title }}</div>
            <div class="mission-body">{{ m.body }}</div>
            <span class="mission-points">{{ m.points }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 12 RECIPE -->
    <section class="section bg-mint">
      <div class="section-inner">
        <span class="section-num">🍳 Try It</span>
        <h2 class="section-title">
          One <em class="pop-tomato">lentil recipe</em> that'll change your mind.
        </h2>
        <p class="section-lede">
          Most of you have never tried a lentil. Try this. It's the gateway dish. Five minutes of prep, twenty minutes on the stove. You can do this.
        </p>

        <div class="recipe-card">
          <div>
            <span class="recipe-tag">EASY · 25 MIN · SERVES 4</span>
            <h3 class="recipe-title">Red Lentil &amp; Tomato Soup</h3>
            <p class="recipe-desc">A no-frills, hard-to-mess-up classic. If you can boil water, you can make this. Pairs with bread, melted cheese on toast, or just a spoon.</p>

            <h4 class="recipe-h4">You need:</h4>
            <ul class="recipe-ingredients">
              <li v-for="ing in ingredients" :key="ing">{{ ing }}</li>
            </ul>
          </div>
          <div>
            <h4 class="recipe-h4">Steps:</h4>
            <ol class="recipe-steps">
              <li v-for="step in steps" :key="step">{{ step }}</li>
            </ol>

            <div class="recipe-stats">
              <div class="recipe-stats-title">🌍 Climate stats vs. equivalent meat soup:</div>
              <div class="recipe-stats-body">
                <strong>10× less CO₂</strong> · <strong>20× less water</strong> · <strong>30× less land</strong><br>
                Costs about CHF 1.50 to make 4 servings.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 13 RECAP -->
    <section class="section bg-sun">
      <div class="section-inner">
        <span class="section-num">💪 The Real Point</span>
        <h2 class="section-title">
          You have more <em class="pop-tomato">power</em> than you think.
        </h2>
        <p class="section-lede">
          Adults usually don't tell kids this. So we'll say it loud:
        </p>

        <div class="recap-banner">
          <div class="recap-emoji">🌍</div>
          <p class="recap-text">
            Every time you choose what to eat, you change the world a tiny bit. Not in a sad guilt way. In a real way. Multiply it by 1,000 classmates. By a million kids your age. <em class="recap-em">That adds up to something huge.</em>
          </p>
        </div>

        <div class="recap-grid">
          <div v-for="t in recapTiles" :key="t.title" class="recap-tile">
            <div class="recap-tile-emoji">{{ t.emoji }}</div>
            <div class="recap-tile-title">{{ t.title }}</div>
            <div class="recap-tile-body">{{ t.body }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <div class="footer">
      <div class="footer-emoji">🌍🥕🌾🌱</div>
      <div class="footer-title">Class 7B, this report is yours.</div>
      <div class="footer-sub">
        Share it with your family. Stick it on the fridge. Argue about lentils at dinner. Take the SEQ again in 3 months and see what changed. Whatever you do, you've earned it.
      </div>
      <div class="footer-meta">SEQ · Swiss Eating Quiz · BFH · v0.1 · May 2026</div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Results11',
  data() {
    return {
      timeInterval: null,
      liveTimes: {
        bern: '—:—',
        brazil: '—:—',
        spain: '—:—',
      },
      wheelResult: {
        emoji: '👇',
        text: "Click the button to get your class's challenge!",
      },
      challenges: [
        { emoji: '🫘', text: 'Everyone tries one lentil-based dish this week. Just one. For science.' },
        { emoji: '🥗', text: 'A whole class day with no meat. Pasta with veg counts. Pizza counts. You got this.' },
        { emoji: '🍇', text: "Try a fruit you've literally never eaten before. Bonus points for weird shapes." },
        { emoji: '🇨🇭', text: "Tomorrow: bring a Swiss-made snack. Bonus: tell the class where it's from." },
        { emoji: '♻️', text: 'Zero food waste at home for 24 hours. Track what you save.' },
        { emoji: '🍫', text: 'Find one fair-trade chocolate bar in your kitchen. Photograph it. Send to teacher.' },
        { emoji: '🍓', text: 'Eat berries this week. Strawberries, raspberries, blueberries — your call.' },
        { emoji: '🥖', text: 'Have a sandwich made with wholemeal bread. Compare it to a white bread one.' },
        { emoji: '💧', text: 'Drink only water for one entire school day. No fruit juice. No fizzy. Pure aqua.' },
        { emoji: '🔍', text: 'Find out where 3 foods in your kitchen come from. Surprise your parents with the answer.' },
      ],
      bigStats: [
        { emoji: '🚗', value: '2.1t', color: 'tomato', label: 'of CO₂ per person per year', body: "That's the same as driving a small car from Bern to Lisbon. Just from eating." },
        { emoji: '🏫', value: '1840', color: 'leaf', label: 'm² of land per person', body: "That's the size of 4 classrooms, used somewhere in the world to feed YOU for one year." },
        { emoji: '🏊', value: '1420', color: 'sky', label: 'm³ of water per person', body: "That's 8 swimming pools. Most of it falls as rain on fields in other countries." },
      ],
      comicPanels: [
        { emoji: '🌸', title: 'April.', body: "A Swiss farmer's apple tree blooms with millions of tiny flowers. Bees show up for the pollen party." },
        { emoji: '☀️', title: 'Summer.', body: 'The flowers turn into baby apples. They grow all summer, drinking rain and sunlight.' },
        { emoji: '🧺', title: 'September.', body: 'Harvest! Workers pick apples by hand and store them in giant fridges that can last all winter.' },
        { emoji: '🎒', title: 'Today.', body: 'The apple travels 80km to your supermarket. You bite it. The cycle repeats. The bees are doing fine.' },
      ],
      producers: [
        {
          headerClass: 'bg-heinz', flag: '🇨🇭 SWITZERLAND', rarity: '★ COMMON',
          avatar: '👨‍🌾', name: 'Heinz, 58', role: 'Swiss dairy farmer with 32 cows. Fourth generation on the farm.',
          stats: [
            { num: '4:45', label: 'Wakes up' },
            { num: '32', label: 'Cows' },
            { num: '40yr', label: 'Doing this' },
          ],
          quote: 'My cows have names. The Gruyère you eat for breakfast? It comes from them.',
        },
        {
          headerClass: 'bg-maria', flag: '🇧🇷 BRAZIL', rarity: '★★ UNCOMMON',
          avatar: '👩‍🌾', name: 'Maria, 41', role: 'Coffee picker on a plantation in Minas Gerais. Picks beans by hand all season.',
          stats: [
            { num: 'CHF 11', label: 'Per day' },
            { num: '2', label: 'Kids' },
            { num: '9000', label: 'km from you' },
          ],
          quote: 'Every coffee bean you drink, I picked it. By hand. One by one.',
        },
        {
          headerClass: 'bg-mohamed', flag: '🇪🇸 SPAIN', rarity: '★★ UNCOMMON',
          avatar: '👨🏽‍🌾', name: 'Mohamed, 27', role: 'Works in giant greenhouses in southern Spain growing tomatoes, peppers, courgettes.',
          stats: [
            { num: '40°C', label: 'Greenhouse heat' },
            { num: '4yr', label: 'In Spain' },
            { num: '2000', label: 'km from you' },
          ],
          quote: 'Those winter tomatoes? Yeah. I grew them. In 40-degree heat.',
        },
        {
          headerClass: 'bg-lucia', flag: '🇪🇨 ECUADOR', rarity: '★★★ RARE',
          avatar: '👩🏽‍🌾', name: 'Lucia, 34', role: 'Sorts and packs bananas in El Oro, Ecuador. About 2,000 bananas per shift.',
          stats: [
            { num: '2000', label: 'Per shift' },
            { num: '2', label: 'Kids' },
            { num: '10000', label: 'km from you' },
          ],
          quote: 'I touch 2,000 bananas a day. One of them might end up in your lunch.',
        },
      ],
      livePlaces: [
        { key: 'bern', flag: '🇨🇭', name: 'Bern', weather: '— °C', action: "You're in school. Heinz is finishing afternoon milking." },
        { key: 'brazil', flag: '🇧🇷', name: 'Minas Gerais', weather: '— °C', action: 'Maria is on coffee plantation, picking beans before sunset.' },
        { key: 'spain', flag: '🇪🇸', name: 'Almería', weather: '— °C', action: 'Mohamed is sorting peppers in the greenhouse.' },
      ],
      countries: [
        { distance: '80 km', flag: '🇨🇭', name: 'Switzerland', foods: 'Most dairy, eggs, potatoes, apples' },
        { distance: '200 km', flag: '🇫🇷', name: 'France', foods: 'Bread wheat, some cheese' },
        { distance: '300 km', flag: '🇮🇹', name: 'Italy', foods: 'Pasta, tomatoes, rice' },
        { distance: '2000 km', flag: '🇪🇸', name: 'Spain', foods: 'Winter veg, oranges, olive oil' },
        { distance: '2500 km', flag: '🇲🇦', name: 'Morocco', foods: 'Tomatoes, green beans' },
        { distance: '5500 km', flag: '🇪🇹', name: 'Ethiopia', foods: 'Coffee beans' },
        { distance: '9000 km', flag: '🇧🇷', name: 'Brazil', foods: 'Coffee, hidden soy in animal feed' },
        { distance: '10000 km', flag: '🇻🇳', name: 'Vietnam', foods: 'Coffee, rice' },
        { distance: '10000 km', flag: '🇪🇨', name: 'Ecuador', foods: 'Bananas, cocoa' },
      ],
      dyks: [
        { emoji: '🐮', title: 'Cows are climate ninjas (in a bad way).', body: "Cows burp methane gas. A lot of it. That gas is <strong>30 times more warming</strong> than CO₂. The world's cows together produce more greenhouse gas than every car, plane, and ship combined. Your dairy yoghurt? Yeah, that." },
        { emoji: '🫘', title: 'A lentil is a tiny superhero.', body: 'To make 1 kg of beef = <strong>25 kg of plant food + 15,000 litres of water</strong>. To make 1 kg of lentils = 1 kg of seed + barely any water. Lentils even <strong>put nitrogen BACK</strong> into the soil. Imagine if your homework gave you energy.' },
        { emoji: '🗑️', title: 'Switzerland throws away 1/3 of its food.', body: "Each Swiss household tosses about <strong>330 kg of food</strong> per year. That's like buying 7 weekly grocery bags and just throwing one straight in the bin. Wild." },
        { emoji: '☕', title: 'Coffee is a climate monster.', body: "One cup of coffee uses <strong>140 litres of water</strong> to make (almost all of it in tropical countries). Your parents drink 3-4 cups a day. Do the math. We'll wait." },
        { emoji: '🍅', title: 'A winter tomato has flown more than you have.', body: 'A tomato in February probably came from Spain, Morocco, or Italy. It traveled <strong>2000 to 3000 km</strong> in a truck. Your summer tomato from Migros Bio? About <strong>50 km</strong>. Same red, very different journey.' },
        { emoji: '📅', title: '"Best before" doesn\'t mean "bad after".', body: '"Mindestens haltbar bis" = the food is at its best until then. <strong>Most food is fine for days, weeks, sometimes months after this date.</strong> Your yoghurt is probably lying to you. (Use the smell test.)' },
      ],
      polls: [
        {
          question: 'How often do you eat lentils, chickpeas or beans?',
          rows: [
            { label: '😶 Never', cls: 't', width: 65, count: 13 },
            { label: '😐 Rarely', cls: 'o', width: 25, count: 5 },
            { label: '🙂 Weekly', cls: '', width: 10, count: 2 },
            { label: '😍 Daily', cls: '', width: 0, count: 0 },
          ],
          note: '13 of you never eat them. Plot twist: lentils are basically the best food for the planet. We need to talk.',
        },
        {
          question: 'How often do you drink sweet drinks like cola or iced tea?',
          rows: [
            { label: '😶 Never', cls: '', width: 25, count: 5 },
            { label: '😐 Rarely', cls: '', width: 35, count: 7 },
            { label: '🙂 Weekly', cls: 'o', width: 30, count: 6 },
            { label: '😍 Daily', cls: 't', width: 10, count: 2 },
          ],
          note: "Class is split in two camps. Doesn't matter which one you're in: it's interesting to see the pattern.",
        },
        {
          question: 'How often do you eat fresh berries?',
          rows: [
            { label: '😶 Never', cls: 't', width: 45, count: 9 },
            { label: '😐 Rarely', cls: 'o', width: 35, count: 7 },
            { label: '🙂 Weekly', cls: '', width: 15, count: 3 },
            { label: '😍 Daily', cls: '', width: 5, count: 1 },
          ],
          note: "Most of you don't eat berries. Fact: they're in season in Switzerland in summer, and they're incredible. Try them.",
        },
      ],
      badges: [
        { locked: false, emoji: '💧', name: 'Hydro Hero', desc: 'Drinks water like a fish', status: '★ UNLOCKED' },
        { locked: false, emoji: '🥬', name: 'Veggie Champ', desc: 'Eats vegetables, no drama', status: '★ UNLOCKED' },
        { locked: false, emoji: '🇨🇭', name: 'Swiss Made', desc: 'Local dairy lover', status: '★ UNLOCKED' },
        { locked: false, emoji: '🚫🥤', name: 'No Sugar Squad', desc: 'Avoids sweet drinks', status: '★ UNLOCKED (5 of you)' },
        { locked: true, emoji: '🫘', name: 'Lentil Legend', desc: 'Eats legumes weekly', status: '🔒 LOCKED' },
        { locked: true, emoji: '🌾', name: 'Whole Grain Guru', desc: 'Prefers wholemeal bread', status: '🔒 LOCKED' },
        { locked: true, emoji: '🐟', name: 'Ocean Friend', desc: 'Eats sustainable fish', status: '🔒 LOCKED' },
        { locked: true, emoji: '🍓', name: 'Berry Brave', desc: 'Tries seasonal berries', status: '🔒 LOCKED' },
      ],
      missions: [
        { level: '', emoji: '🍌', title: 'Buy fair-trade bananas this month.', body: 'Look for the Max Havelaar logo. Costs ~30 ct more per kg. Helps Lucia.', points: '+10 PTS · EASY' },
        { level: '', emoji: '🥪', title: 'Take leftovers for lunch.', body: "Yesterday's pasta. Friday's pizza. Eats good. Saves food. Costs nothing.", points: '+10 PTS · EASY' },
        { level: '', emoji: '💧', title: 'Bring a water bottle. Refill it.', body: 'Tap water in Switzerland is some of the best in the world. Skip the plastic bottles.', points: '+10 PTS · EASY' },
        { level: 'medium', emoji: '🍱', title: 'Cook one meal at home this month.', body: 'From actual ingredients. Asking the internet for a recipe counts. Pizza from scratch also counts.', points: '+25 PTS · MEDIUM' },
        { level: 'medium', emoji: '📅', title: 'Eat seasonal for one week.', body: "Look up what's in season in Switzerland this month. Eat that. Avoid the imported stuff.", points: '+25 PTS · MEDIUM' },
        { level: 'medium', emoji: '🐟', title: 'Try MSC-certified fish.', body: 'MSC = ocean-friendly. The little blue tick on the package. Salmon, tuna, fish fingers.', points: '+25 PTS · MEDIUM' },
        { level: 'hard', emoji: '🌱', title: 'Go vegetarian for a whole week.', body: "Yes, the whole week. No meat. Yes, even sausages count. You'd be surprised: it's not hard.", points: '+50 PTS · HARD' },
        { level: 'hard', emoji: '📈', title: "Track your family's food waste for a week.", body: 'A bowl on the counter. Write down everything you throw away. Then halve it next week.', points: '+50 PTS · HARD' },
        { level: 'hard', emoji: '🌾', title: 'Grow something edible.', body: 'Basil on a windowsill. Tomatoes on a balcony. Salad in a pot. Watch a plant feed you. Magic.', points: '+50 PTS · HARD' },
      ],
      ingredients: [
        '🫘 1 cup red lentils',
        '🥫 1 can chopped tomatoes',
        '🧅 1 onion, chopped',
        '🧄 2 garlic cloves',
        '🧂 Salt, pepper, paprika',
        '💧 4 cups water or veg stock',
        '🫒 Olive oil',
      ],
      steps: [
        'Cook the onion and garlic in olive oil until soft (~5 min).',
        'Add lentils, tomatoes, water, and spices.',
        'Bring to a boil. Lower heat. Simmer 20 min, stirring sometimes.',
        'Done. Add salt. Eat with bread.',
      ],
      recapTiles: [
        { emoji: '🥗', title: 'Your plate matters.', body: 'Even one meal a week makes a real difference.' },
        { emoji: '🗣️', title: 'Your voice matters.', body: "Tell your family what you learned. They'll listen." },
        { emoji: '⏰', title: 'Your time matters.', body: "You're 12. Everything you change today, lasts decades." },
      ],
    };
  },
  mounted() {
    this.updateTimes();
    this.timeInterval = setInterval(this.updateTimes, 60000);
  },
  beforeUnmount() {
    if (this.timeInterval) clearInterval(this.timeInterval);
  },
  methods: {
    updateTimes() {
      const tz = {
        bern: 'Europe/Zurich',
        brazil: 'America/Sao_Paulo',
        spain: 'Europe/Madrid',
      };
      const next = { ...this.liveTimes };
      for (const [key, zone] of Object.entries(tz)) {
        try {
          next[key] = new Date().toLocaleTimeString('en-GB', {
            timeZone: zone,
            hour: '2-digit',
            minute: '2-digit',
          });
        } catch (e) {
          next[key] = '—:—';
        }
      }
      this.liveTimes = next;
    },
    spinWheel() {
      this.wheelResult = { emoji: '⏳', text: 'Spinning...' };
      setTimeout(() => {
        const c = this.challenges[Math.floor(Math.random() * this.challenges.length)];
        this.wheelResult = { emoji: c.emoji, text: c.text };
      }, 800);
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&family=Caveat:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap');

.results11-wrap {
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --paper: #FFF8EE;
  --card: #FFFFFF;
  --sun: #FFD93D;
  --tomato: #FF6B6B;
  --leaf: #6BCB77;
  --sky: #4D96FF;
  --plum: #9B59E2;
  --orange: #FF9F45;
  --pink: #FFB4D8;
  --mint: #B4E4D8;
  --coral: #FF8E72;

  --shadow-hard: 6px 6px 0 var(--ink);
  --shadow-medium: 4px 4px 0 var(--ink);
  --shadow-soft: 0 4px 12px rgba(0,0,0,0.08);

  font-family: 'Fredoka', sans-serif;
  font-size: 16px;
  line-height: 1.55;
  color: var(--ink);
  background: var(--paper);
  background-image:
    radial-gradient(circle at 15% 20%, rgba(255,217,61,0.12) 0%, transparent 40%),
    radial-gradient(circle at 85% 75%, rgba(107,203,119,0.12) 0%, transparent 40%);
  min-height: 100%;
}
.results11-wrap *,
.results11-wrap *::before,
.results11-wrap *::after {
  box-sizing: border-box;
}

/* TOPBAR */
.topbar {
  background: var(--ink);
  color: white;
  padding: 14px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
}
.topbar-left { display: flex; align-items: center; gap: 12px; }
.topbar-emoji {
  font-size: 28px;
  animation: bounce 2s ease-in-out infinite;
}
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

/* HERO */
.hero {
  padding: 60px 32px 80px;
  text-align: center;
  position: relative;
  background:
    radial-gradient(circle at 20% 30%, var(--sun) 0%, transparent 25%),
    radial-gradient(circle at 80% 70%, var(--leaf) 0%, transparent 25%),
    radial-gradient(circle at 50% 50%, var(--tomato) 0%, transparent 20%),
    var(--paper);
  overflow: hidden;
}
.hero-tag {
  display: inline-block;
  background: var(--ink);
  color: var(--sun);
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 8px 18px;
  border-radius: 100px;
  margin-bottom: 24px;
  transform: rotate(-2deg);
}
.hero-title {
  font-size: 76px;
  font-weight: 700;
  line-height: 0.95;
  letter-spacing: -0.03em;
  margin-bottom: 16px;
  position: relative;
  z-index: 2;
}
.hero-title .word-1 { color: var(--tomato); }
.hero-title .word-2 { color: var(--leaf); }
.hero-title .word-3 { color: var(--sky); display: inline-block; transform: rotate(-3deg); }
.hero-title em {
  font-family: 'Caveat', cursive;
  font-style: normal;
  color: var(--plum);
  font-weight: 700;
}
.hero-sub {
  font-size: 22px;
  max-width: 620px;
  margin: 0 auto 36px;
  color: var(--ink-soft);
  line-height: 1.5;
  font-weight: 400;
}
.hero-class-card {
  background: white;
  border: 3px solid var(--ink);
  border-radius: 20px;
  padding: 20px 32px;
  display: inline-flex;
  align-items: center;
  gap: 20px;
  box-shadow: var(--shadow-hard);
  transform: rotate(-1deg);
}
.hero-class-card-num {
  font-size: 48px;
  font-weight: 700;
  color: var(--plum);
  line-height: 1;
}
.hero-class-card-label {
  text-align: left;
  font-size: 14px;
  color: var(--ink-soft);
}
.hero-class-card-label strong { font-size: 18px; color: var(--ink); display: block; }

.float {
  position: absolute;
  font-size: 56px;
  z-index: 1;
  filter: drop-shadow(2px 2px 0 rgba(0,0,0,0.1));
}
.float-1 { top: 80px; left: 8%; animation: float 5s ease-in-out infinite; }
.float-2 { top: 120px; right: 10%; animation: float 6s ease-in-out infinite reverse; }
.float-3 { bottom: 60px; left: 12%; animation: float 7s ease-in-out infinite; }
.float-4 { bottom: 80px; right: 14%; animation: float 5.5s ease-in-out infinite reverse; }
@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(8deg); }
}

/* SECTIONS */
.section { padding: 60px 32px; position: relative; }
.section-inner { max-width: 920px; margin: 0 auto; }
.section.bg-sun { background: var(--sun); }
.section.bg-leaf { background: var(--leaf); color: white; }
.section.bg-sky { background: var(--sky); color: white; }
.section.bg-plum { background: var(--plum); color: white; }
.section.bg-coral { background: var(--coral); color: white; }
.section.bg-mint { background: var(--mint); }
.section.bg-pink { background: var(--pink); }
.section.bg-dark { background: var(--ink); color: white; }
.section.bg-paper { background: var(--paper); }

.section-num {
  display: inline-block;
  background: var(--ink);
  color: white;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 100px;
  margin-bottom: 16px;
}
.section.bg-dark .section-num { background: var(--sun); color: var(--ink); }
.section.bg-leaf .section-num,
.section.bg-sky .section-num,
.section.bg-plum .section-num,
.section.bg-coral .section-num { background: white; color: var(--ink); }
.section-title {
  font-size: 52px;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
  margin-bottom: 16px;
  max-width: 800px;
}
.section-title em {
  font-family: 'Caveat', cursive;
  font-style: normal;
  font-weight: 700;
}
.section-title em.pop-tomato { color: var(--tomato); }
.section-title em.pop-leaf { color: var(--leaf); }
.section-title em.pop-sky { color: var(--sky); }
.section-title em.pop-sun { color: var(--sun); }
.section-title em.pop-plum { color: var(--plum); }
.section-lede {
  font-size: 19px;
  max-width: 700px;
  margin-bottom: 36px;
  opacity: 0.92;
  font-weight: 400;
}

/* BIG STATS */
.big-stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin: 36px 0;
}
.big-stat {
  background: white;
  border: 3px solid var(--ink);
  border-radius: 24px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: var(--shadow-hard);
  position: relative;
}
.big-stat:nth-child(1) { transform: rotate(-1.5deg); }
.big-stat:nth-child(2) { transform: rotate(1deg); }
.big-stat:nth-child(3) { transform: rotate(-0.5deg); }
.big-stat-emoji { font-size: 56px; margin-bottom: 8px; line-height: 1; }
.big-stat-value {
  font-size: 56px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 8px;
  letter-spacing: -0.02em;
}
.big-stat-value.tomato { color: var(--tomato); }
.big-stat-value.leaf { color: var(--leaf); }
.big-stat-value.sky { color: var(--sky); }
.big-stat-value.plum { color: var(--plum); }
.big-stat-value.orange { color: var(--orange); }
.big-stat-label {
  font-size: 15px;
  color: var(--ink-soft);
  line-height: 1.4;
}
.big-stat-label strong { display: block; font-size: 17px; color: var(--ink); margin-bottom: 4px; }

.poster-number {
  font-size: 200px;
  font-weight: 700;
  line-height: 0.9;
  letter-spacing: -0.05em;
  text-align: center;
  margin: 20px 0;
  color: var(--ink);
}
.poster-caption {
  text-align: center;
  font-size: 22px;
  font-weight: 500;
  max-width: 600px;
  margin: 0 auto;
}
.poster-caption-em {
  font-family: 'Caveat', cursive;
  color: var(--tomato);
  font-weight: 700;
  font-size: 28px;
  font-style: normal;
}

/* COMIC STRIP */
.comic-strip {
  background: white;
  border: 3px solid var(--ink);
  border-radius: 24px;
  padding: 24px;
  margin: 24px 0;
  box-shadow: var(--shadow-hard);
}
.comic-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
.comic-panel {
  background: var(--paper);
  border: 2px solid var(--ink);
  border-radius: 12px;
  padding: 18px 14px;
  text-align: center;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}
.comic-panel-emoji { font-size: 64px; line-height: 1; }
.comic-panel-num {
  background: var(--tomato);
  color: white;
  font-weight: 700;
  font-size: 14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  border: 2px solid var(--ink);
}
.comic-panel-text {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
}
.divider {
  text-align: center;
  font-size: 36px;
  margin: 36px 0;
  letter-spacing: 0.2em;
}
.apple-compare {
  font-size: 19px;
  text-align: center;
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.5;
}
.apple-compare-far { color: var(--tomato); }

/* PRODUCER CARDS */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}
.producer-card {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: var(--shadow-hard);
  position: relative;
}
.producer-card:nth-child(odd) { transform: rotate(-0.5deg); }
.producer-card:nth-child(even) { transform: rotate(0.5deg); }
.producer-card-header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 3px solid var(--ink);
}
.producer-card-header.bg-heinz { background: var(--mint); }
.producer-card-header.bg-maria { background: var(--coral); color: white; }
.producer-card-header.bg-mohamed { background: var(--sun); }
.producer-card-header.bg-lucia { background: var(--pink); }
.producer-card-flag {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.05em;
}
.producer-card-rarity {
  font-size: 10px;
  font-weight: 700;
  background: var(--ink);
  color: var(--sun);
  padding: 4px 10px;
  border-radius: 100px;
  letter-spacing: 0.1em;
}
.producer-card-body {
  padding: 20px;
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 16px;
  align-items: start;
}
.producer-avatar {
  width: 100px;
  height: 100px;
  border-radius: 20px;
  background: var(--paper);
  border: 2px solid var(--ink);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.producer-avatar-emoji { font-size: 64px; }
.producer-name { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
.producer-role { font-size: 13px; color: var(--ink-soft); margin-bottom: 12px; line-height: 1.4; }
.producer-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  background: var(--paper);
  padding: 10px;
  border-radius: 10px;
  border: 2px solid var(--ink);
}
.producer-stat { text-align: center; }
.producer-stat-num {
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}
.producer-stat-label {
  font-size: 10px;
  color: var(--ink-soft);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 2px;
}
.producer-card-quote {
  padding: 14px 20px;
  background: var(--paper);
  border-top: 2px solid var(--ink);
  font-family: 'Caveat', cursive;
  font-size: 18px;
  font-weight: 600;
}
.producer-footer {
  margin-top: 36px;
  font-size: 19px;
  text-align: center;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

/* LIVE WIDGET */
.live-widget {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 24px;
  padding: 28px;
  box-shadow: var(--shadow-hard);
  margin: 24px 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
}
.live-place {
  text-align: center;
  padding: 18px;
  background: var(--paper);
  border: 2px solid var(--ink);
  border-radius: 16px;
}
.live-place-flag { font-size: 36px; margin-bottom: 8px; }
.live-place-name { font-weight: 700; font-size: 16px; margin-bottom: 4px; }
.live-place-time { font-size: 12px; color: var(--ink-soft); margin-bottom: 12px; font-family: monospace; }
.live-place-weather {
  background: white;
  border: 2px solid var(--ink);
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 15px;
}
.live-place-action {
  margin-top: 8px;
  font-size: 12px;
  color: var(--ink-soft);
  font-style: italic;
}

/* COUNTRY GRID */
.country-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-top: 24px;
}
.country-tile {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 16px;
  padding: 18px;
  box-shadow: var(--shadow-medium);
  text-align: center;
  position: relative;
}
.country-flag {
  font-size: 40px;
  line-height: 1;
  margin-bottom: 8px;
}
.country-name {
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 6px;
}
.country-foods {
  font-size: 13px;
  color: var(--ink-soft);
  line-height: 1.4;
}
.country-distance {
  position: absolute;
  top: -10px;
  right: -10px;
  background: var(--tomato);
  color: white;
  border: 2px solid var(--ink);
  border-radius: 100px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  transform: rotate(8deg);
}
.world-footer {
  margin-top: 32px;
  font-size: 22px;
  text-align: center;
  font-family: 'Caveat', cursive;
  font-weight: 600;
}

/* DID YOU KNOW */
.dyk-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
  margin-top: 24px;
}
.dyk-card {
  background: white;
  border: 3px solid var(--ink);
  border-radius: 20px;
  padding: 24px;
  box-shadow: var(--shadow-medium);
  position: relative;
}
.dyk-card:nth-child(4n+1) { transform: rotate(-1deg); }
.dyk-card:nth-child(4n+2) { transform: rotate(0.8deg); }
.dyk-card:nth-child(4n+3) { transform: rotate(0.6deg); }
.dyk-card:nth-child(4n+4) { transform: rotate(-0.7deg); }
.dyk-badge {
  position: absolute;
  top: -14px;
  left: 20px;
  background: var(--sun);
  color: var(--ink);
  border: 2px solid var(--ink);
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.dyk-emoji { font-size: 48px; margin-bottom: 12px; line-height: 1; }
.dyk-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.2;
}
.dyk-body { font-size: 14px; line-height: 1.5; color: var(--ink-soft); }
.dyk-body :deep(strong) { color: var(--ink); }

/* POLLS */
.poll-card {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 24px;
  padding: 28px;
  box-shadow: var(--shadow-hard);
  margin: 24px 0;
}
.poll-question {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 16px;
  line-height: 1.2;
}
.poll-bar-row {
  display: grid;
  grid-template-columns: 140px 1fr 60px;
  gap: 14px;
  align-items: center;
  margin-bottom: 12px;
}
.poll-bar-label { font-weight: 500; font-size: 14px; }
.poll-bar-track {
  background: var(--paper);
  border: 2px solid var(--ink);
  border-radius: 100px;
  height: 28px;
  position: relative;
  overflow: hidden;
}
.poll-bar-fill {
  height: 100%;
  border-radius: 100px;
  background: var(--leaf);
  border-right: 2px solid var(--ink);
}
.poll-bar-fill.t { background: var(--tomato); }
.poll-bar-fill.s { background: var(--sky); }
.poll-bar-fill.p { background: var(--plum); }
.poll-bar-fill.o { background: var(--orange); }
.poll-bar-count {
  font-weight: 700;
  font-size: 15px;
  text-align: center;
}
.poll-note {
  margin-top: 14px;
  font-size: 14px;
  font-style: italic;
  color: var(--ink-soft);
}

/* AI STORY */
.ai-story {
  background: linear-gradient(135deg, var(--plum) 0%, var(--sky) 100%);
  color: white;
  border: 3px solid var(--ink);
  border-radius: 28px;
  padding: 36px;
  box-shadow: var(--shadow-hard);
  margin: 24px 0;
  position: relative;
  overflow: hidden;
}
.ai-story::before {
  content: '✨';
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 60px;
  opacity: 0.4;
}
.ai-story-tag {
  display: inline-block;
  background: var(--sun);
  color: var(--ink);
  padding: 6px 16px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 14px;
}
.ai-story-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 14px;
  line-height: 1.15;
}
.ai-story-body {
  font-size: 17px;
  line-height: 1.65;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
}
.ai-story-body p { margin: 0 0 12px 0; }
.ai-story-body p:last-child { margin-bottom: 0; }
.ai-story-body em {
  font-family: 'Caveat', cursive;
  font-style: normal;
  color: var(--sun);
  font-weight: 700;
}

/* BADGES */
.badges-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-top: 24px;
}
.badge {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 20px;
  padding: 20px 14px;
  text-align: center;
  box-shadow: var(--shadow-medium);
  position: relative;
}
.badge.unlocked { background: var(--sun); }
.badge.locked { opacity: 0.55; background: #E5E5E0; }
.badge-emoji { font-size: 48px; line-height: 1; margin-bottom: 8px; }
.badge-name {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.badge-desc { font-size: 11px; color: var(--ink-soft); line-height: 1.3; }
.badge-status {
  margin-top: 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.badge.unlocked .badge-status { color: var(--leaf); }
.badge.locked .badge-status { color: var(--ink-soft); }
.badges-footer {
  margin-top: 28px;
  text-align: center;
  font-family: 'Caveat', cursive;
  font-weight: 600;
  font-size: 24px;
}

/* WHEEL */
.wheel-card {
  background: var(--plum);
  color: white;
  border: 3px solid var(--ink);
  border-radius: 28px;
  padding: 36px;
  box-shadow: var(--shadow-hard);
  text-align: center;
  margin: 24px 0;
}
.wheel-emoji { font-size: 84px; margin-bottom: 14px; animation: spin 3s ease-in-out infinite; display: inline-block; }
@keyframes spin {
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(20deg); }
}
.wheel-title { font-size: 36px; font-weight: 700; margin-bottom: 8px; }
.wheel-sub { font-size: 17px; opacity: 0.9; margin-bottom: 24px; }
.wheel-button {
  background: var(--sun);
  color: var(--ink);
  border: 3px solid var(--ink);
  padding: 16px 40px;
  font-family: 'Fredoka', sans-serif;
  font-size: 20px;
  font-weight: 700;
  border-radius: 100px;
  cursor: pointer;
  box-shadow: var(--shadow-medium);
  transition: all 0.15s;
}
.wheel-button:hover { transform: translateY(-3px); box-shadow: 6px 6px 0 var(--ink); }
.wheel-button:active { transform: translateY(0); box-shadow: 2px 2px 0 var(--ink); }
.wheel-result {
  margin-top: 24px;
  background: white;
  color: var(--ink);
  padding: 20px 28px;
  border: 3px solid var(--ink);
  border-radius: 18px;
  display: inline-block;
  min-width: 320px;
  font-size: 18px;
  font-weight: 500;
}
.wheel-result-emoji { font-size: 36px; display: block; margin-bottom: 8px; }

/* MISSIONS */
.mission-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin: 24px 0;
}
.mission {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 20px;
  padding: 22px;
  box-shadow: var(--shadow-medium);
  position: relative;
}
.mission-emoji { font-size: 44px; margin-bottom: 10px; line-height: 1; }
.mission-title {
  font-size: 17px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.25;
}
.mission-body { font-size: 13px; color: var(--ink-soft); line-height: 1.5; margin-bottom: 14px; }
.mission-points {
  display: inline-block;
  background: var(--leaf);
  color: white;
  padding: 4px 10px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  border: 2px solid var(--ink);
}
.mission.medium .mission-points { background: var(--orange); }
.mission.hard .mission-points { background: var(--tomato); }

/* RECIPE */
.recipe-card {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 24px;
  padding: 32px;
  box-shadow: var(--shadow-hard);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}
.recipe-tag {
  background: var(--tomato);
  color: white;
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.recipe-title {
  font-size: 28px;
  font-weight: 700;
  margin: 14px 0 18px;
  line-height: 1.15;
}
.recipe-desc {
  font-size: 14px;
  color: var(--ink-soft);
  margin-bottom: 18px;
  line-height: 1.55;
}
.recipe-h4 {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
}
.recipe-ingredients {
  list-style: none;
  padding: 0;
  font-size: 14px;
  line-height: 1.8;
  margin: 0;
}
.recipe-steps {
  padding-left: 20px;
  font-size: 14px;
  line-height: 1.75;
  margin: 0;
}
.recipe-stats {
  margin-top: 20px;
  background: var(--paper);
  border: 2px solid var(--ink);
  border-radius: 14px;
  padding: 14px 18px;
}
.recipe-stats-title {
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 4px;
}
.recipe-stats-body {
  font-size: 13px;
  line-height: 1.5;
  color: var(--ink-soft);
}

/* RECAP */
.recap-banner {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 28px;
  padding: 40px;
  box-shadow: var(--shadow-hard);
  margin: 24px 0;
}
.recap-emoji {
  font-size: 64px;
  text-align: center;
  margin-bottom: 16px;
}
.recap-text {
  font-size: 22px;
  line-height: 1.55;
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
  font-weight: 500;
}
.recap-em {
  font-family: 'Caveat', cursive;
  font-style: normal;
  color: var(--tomato);
  font-weight: 700;
  font-size: 28px;
}
.recap-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  margin-top: 32px;
}
.recap-tile {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  box-shadow: var(--shadow-medium);
}
.recap-tile-emoji {
  font-size: 48px;
  margin-bottom: 12px;
}
.recap-tile-title {
  font-weight: 700;
  font-size: 17px;
  margin-bottom: 6px;
}
.recap-tile-body {
  font-size: 13px;
  color: var(--ink-soft);
}

/* FOOTER */
.footer {
  background: var(--ink);
  color: white;
  padding: 48px 32px;
  text-align: center;
}
.footer-emoji { font-size: 56px; margin-bottom: 16px; }
.footer-title { font-size: 28px; font-weight: 700; margin-bottom: 12px; }
.footer-sub { font-size: 15px; opacity: 0.7; max-width: 480px; margin: 0 auto; line-height: 1.6; }
.footer-meta { font-size: 11px; opacity: 0.5; margin-top: 24px; letter-spacing: 0.1em; text-transform: uppercase; }
</style>
